import { useState } from "react";
import { FileText, CheckCircle, Upload, Link, Sparkles } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { SearchableKnowledgeBase } from "@/components/knowledge-base";
import CompactUploadSection from "@/components/knowledge-base/CompactUploadSection";
import { KnowledgeBase as KnowledgeBaseItem } from '@/types/knowledge/knowledge-base';
import { DeferredDocumentAction } from '@/types/knowledge/deferred-operations';
import { toast } from "sonner";
import { ReviewSectionCard } from "./ReviewSectionCard";

interface ConfirmationKnowledgeSectionProps {
  selectedKnowledgeBase: KnowledgeBaseItem[];
  onDocumentToggle: (doc: any, checked: boolean) => void;
  onBulkToggle?: (docs: any[], checked: boolean) => void;
  isProcessing?: boolean;
  pendingDocumentIds?: string[];
  onPendingDocumentsChange?: (ids: string[]) => void;
  deferredActions?: DeferredDocumentAction[];
  onDeferredActionsChange?: (actions: DeferredDocumentAction[]) => void;
  /** Advance the wizard past this step ("I'll add documents later"). */
  onSkip?: () => void;
}

export const ConfirmationKnowledgeSection = ({
  selectedKnowledgeBase,
  onDocumentToggle,
  onBulkToggle,
  isProcessing = false,
  pendingDocumentIds = [],
  onPendingDocumentsChange,
  deferredActions = [],
  onDeferredActionsChange,
  onSkip,
}: ConfirmationKnowledgeSectionProps) => {
  const [showUpload, setShowUpload] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [generateQuery, setGenerateQuery] = useState("");

  const handleGenerateSubmit = () => {
    if (!generateQuery.trim()) {
      toast.error("Please enter a topic or description");
      return;
    }

    // Add to deferred actions
    if (onDeferredActionsChange) {
      const newAction: DeferredDocumentAction = {
        type: 'generate',
        data: {
          query: generateQuery,
          title: `Generated: ${generateQuery}`,
        },
      };
      onDeferredActionsChange([...deferredActions, newAction]);
      toast.success(`Document will be auto-generated after genie creation`);
      setGenerateQuery("");
    }
  };

  const handleRemoveDeferredAction = (index: number) => {
    if (onDeferredActionsChange) {
      const updated = deferredActions.filter((_, i) => i !== index);
      onDeferredActionsChange(updated);
      toast.info("Deferred action removed");
    }
  };

  return (
    <ReviewSectionCard
      title="Knowledge Base"
      subtitle="Add documents to help your genie provide accurate, context-aware responses. Supported formats: PDF, text documents, and web content."
      genieIcon="book-open"
      brand="#4E9CFF"
    >
      <div className="space-y-6">
        {/* Informative Message - Documents are Optional */}
        <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-[#4E9CFF]/10 to-[#FF4DB8]/10 border-2 border-[#4E9CFF]/30 rounded-lg">
          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white flex items-center justify-center text-xs font-bold mt-0.5">
            i
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">
              Documents are completely optional at this stage
            </p>
            <p className="text-xs text-gray-700 mt-1">
              You can add, manage, and update your knowledge base anytime from your genie's settings. Documents upload in the background - feel free to continue even if they're still processing.
            </p>
          </div>
        </div>

        {/* Deferred Actions Status */}
        {deferredActions.length > 0 && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 flex-1">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <p className="text-sm text-blue-900">
                {deferredActions.length} document{deferredActions.length > 1 ? 's' : ''} will be processed after genie creation
              </p>
            </div>
          </div>
        )}

        {/* Selected Count Badge */}
        <div className="flex items-center gap-2">
          <Label className="text-lg font-semibold text-gray-900">Selected Documents</Label>
          <Badge variant="outline" className="px-3 py-1.5 border-[#4E9CFF]/40 text-[#4E9CFF] bg-[#4E9CFF]/5">
            {selectedKnowledgeBase.length} selected
          </Badge>
          {deferredActions.length > 0 && (
            <Badge variant="outline" className="px-3 py-1.5 border-blue-500/40 text-blue-700 bg-blue-50">
              +{deferredActions.length} queued
            </Badge>
          )}
        </div>

        <div className="space-y-6">
          {/* Selected Documents Display */}
          {selectedKnowledgeBase.length > 0 && (
            <div>
              <div className="space-y-2">
                {selectedKnowledgeBase.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 p-4 bg-gradient-to-r from-[#4E9CFF]/5 to-[#FF4DB8]/5 rounded-lg border border-[#4E9CFF]/20"
                  >
                    <CheckCircle className="w-5 h-5 text-[#4E9CFF] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-medium text-gray-900 truncate">{doc.name || "Untitled Document"}</div>
                      {doc.type && <div className="text-sm text-gray-600 capitalize">{doc.type}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deferred Actions Display */}
          {deferredActions.length > 0 && (
            <div>
              <Label className="text-base font-semibold text-gray-900 mb-2 block">Queued Actions</Label>
              <div className="space-y-2">
                {deferredActions.map((action, index) => {
                  const actionData = action.data as any;
                  let displayName = '';
                  let actionType = '';

                  if (action.type === 'upload-file') {
                    displayName = actionData.fileName;
                    actionType = 'Upload PDF';
                  } else if (action.type === 'upload-url') {
                    displayName = actionData.fileName || actionData.url;
                    actionType = 'Import URL';
                  } else if (action.type === 'generate') {
                    displayName = actionData.query;
                    actionType = 'Auto-generate';
                  } else if (action.type === 'quick-research') {
                    displayName = actionData.query;
                    actionType = 'Quick Research';
                  } else if (action.type === 'deep-research') {
                    displayName = actionData.query;
                    actionType = 'Deep Research';
                  } else if (action.type === 'youtube') {
                    displayName = actionData.fileName || actionData.url;
                    actionType = 'YouTube Transcript';
                  } else if (action.type === 'website') {
                    displayName = actionData.fileName || actionData.url;
                    actionType = 'Website Content';
                  } else if (action.type === 'audio') {
                    displayName = actionData.fileName;
                    actionType = 'Audio Transcription';
                  } else if (action.type === 'document') {
                    displayName = actionData.fileName;
                    actionType = 'Document Extraction';
                  } else if (action.type === 'notes') {
                    displayName = actionData.title || 'Notes';
                    actionType = 'Create Notes';
                  } else if (action.type === 'web-scraping') {
                    displayName = actionData.fileName || actionData.url;
                    actionType = 'Web Scraping';
                  }

                  return (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200"
                    >
                      <Sparkles className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{displayName}</div>
                        <div className="text-xs text-blue-600">{actionType}</div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveDeferredAction(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Remove
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowUpload(!showUpload);
                  setShowSearch(false);
                  setShowGenerate(false);
                }}
                className="flex items-center justify-center gap-2 h-11 text-base border-[#4E9CFF]/40 text-[#4E9CFF] hover:bg-[#4E9CFF]/5"
              >
                <Upload className="w-4 h-4" />
                {showUpload ? "Hide" : "Upload"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowSearch(!showSearch);
                  setShowUpload(false);
                  setShowGenerate(false);
                }}
                className="flex items-center justify-center gap-2 h-11 text-base border-[#FF4DB8]/40 text-[#FF4DB8] hover:bg-[#FF4DB8]/5"
              >
                <Link className="w-4 h-4" />
                {showSearch ? "Hide" : "Link Existing"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowGenerate(!showGenerate);
                  setShowUpload(false);
                  setShowSearch(false);
                }}
                className="flex items-center justify-center gap-2 h-11 text-base border-purple-500/40 text-purple-600 hover:bg-purple-500/5"
              >
                <Sparkles className="w-4 h-4" />
                {showGenerate ? "Hide" : "Auto-Generate"}
              </Button>
            </div>

            {/* Skip for Now Option */}
            {selectedKnowledgeBase.length === 0 && deferredActions.length === 0 && !showUpload && !showSearch && !showGenerate && (
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-2">
                  Want to add documents later?
                </p>
                <Button
                  variant="ghost"
                  onClick={() => {
                    toast.success("No problem! You can add documents anytime from your genie settings.");
                    onSkip?.();
                  }}
                  className="text-sm bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent hover:opacity-80 font-semibold"
                >
                  I'll add documents later →
                </Button>
              </div>
            )}
          </div>

          {/* Upload Section */}
          {showUpload && (
            <div className="rounded-lg">
              <CompactUploadSection
                onDeferredActionAdd={(action) => {
                  if (onDeferredActionsChange) {
                    onDeferredActionsChange([...deferredActions, action]);
                  }
                }}
              />
            </div>
          )}

          {/* Generate Section */}
          {showGenerate && (
            <div className="p-5 bg-purple-50 rounded-lg border border-purple-200">
              <Label className="text-base font-semibold text-gray-900 mb-2 block">
                Auto-Generate Document
              </Label>
              <p className="text-sm text-gray-600 mb-4">
                AI will search the web and generate a comprehensive document based on your topic
              </p>
              <div className="space-y-3">
                <Input
                  type="text"
                  placeholder="e.g., Tesla Model 3 user manual, Italian restaurant menu guide..."
                  value={generateQuery}
                  onChange={(e) => setGenerateQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleGenerateSubmit();
                    }
                  }}
                  className="w-full"
                />
                <Button
                  onClick={handleGenerateSubmit}
                  disabled={!generateQuery.trim()}
                  className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Queue Generation
                </Button>
              </div>
            </div>
          )}

          {/* Search & Link Section */}
          {showSearch && (
            <div className="p-5 bg-gray-50 rounded-lg border border-gray-200">
              <Label className="text-base font-semibold text-gray-900 mb-2 block">
                Search Your Knowledge Base
              </Label>
              <p className="text-sm text-gray-600 mb-4">
                Find and link documents from your existing knowledge base to this genie
              </p>
              <SearchableKnowledgeBase
                selectedDocuments={selectedKnowledgeBase}
                onDocumentToggle={onDocumentToggle}
                onBulkToggle={onBulkToggle}
                maxHeight="max-h-64"
              />
            </div>
          )}

          {/* Empty State */}
          {selectedKnowledgeBase.length === 0 && deferredActions.length === 0 && !showUpload && !showSearch && !showGenerate && (
            <div className="text-center py-10 px-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <h4 className="text-lg font-semibold text-gray-900 mb-2">No knowledge base documents yet</h4>
              <p className="text-base text-gray-600">
                Upload, link existing documents, or auto-generate one to give your genie context and information
              </p>
            </div>
          )}

        </div>
      </div>
    </ReviewSectionCard>
  );
};
